import { useState } from "react";
import { X, Loader2, Film, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function RunwayModal({ design, onClose, onUpdate }) {
  const [videoPath, setVideoPath] = useState(design.runway_video_path || null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/designs/${design.id}/runway-video`);
      setVideoPath(data.video_path);
      onUpdate && onUpdate({ ...design, runway_video_path: data.video_path });
      toast.success(data.cached ? "Video ready" : "Runway rendered");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Video generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-zinc-950/95 backdrop-blur-md flex items-center justify-center px-4" data-testid="runway-modal">
      <div className="w-full max-w-3xl bg-zinc-950 text-zinc-100 border border-zinc-800 relative">
        <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 bg-zinc-100 text-zinc-950 hover:bg-lime-400 flex items-center justify-center z-10" data-testid="runway-close-btn">
          <X className="w-4 h-4" />
        </button>

        <div className="p-8">
          <div className="overline text-lime-400 mb-2">§ Runway — Sora 2</div>
          <h3 className="font-serif text-4xl leading-tight">{design.title}</h3>

          {videoPath ? (
            <div className="mt-6" data-testid="runway-video-player-wrap">
              <video
                src={fileUrl(videoPath)}
                controls
                autoPlay
                loop
                playsInline
                className="w-full aspect-video max-h-[70vh] mx-auto bg-black"
                data-testid="runway-video-player"
              />
              <div className="mt-4 flex items-center justify-between text-sm">
                <div className="overline text-zinc-500">4s · Landscape · Sora 2</div>
                <a href={fileUrl(videoPath)} download target="_blank" rel="noreferrer" className="overline text-lime-400 hover:text-zinc-100" data-testid="runway-download-btn">
                  Download ↗
                </a>
              </div>
            </div>
          ) : (
            <div className="mt-8 text-center py-12 border border-zinc-800">
              <Film className="w-12 h-12 mx-auto text-zinc-500 mb-6" strokeWidth={1.2} />
              <div className="font-serif italic text-3xl mb-3">Bring the look to life.</div>
              <p className="text-zinc-400 max-w-md mx-auto mb-8">Sora 2 will render a 4-second editorial product film of this piece — slow parallax on the fabric and silhouette, no people (moderation-safe).</p>
              <button
                onClick={generate}
                disabled={busy}
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-full overline ${busy ? "bg-zinc-800 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-100"} transition-colors`}
                data-testid="runway-generate-btn"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering (2-5 min)</> : <><Sparkles className="w-4 h-4" /> Generate Runway Clip</>}
              </button>
              <p className="text-xs text-zinc-600 mt-6">Note: Sora 2 renders a fresh product film from the design brief — it doesn&apos;t composite your saved render. Takes 1–5 min.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
